import React from 'react'
import { Stack, Button, Typography, Grid, IconButton } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { NumberField, TextField } from 'src/Libs/inputs/react-hook-form'
import { useApp } from 'src/Hooks'
import DynamicTariffTimerPicker from './DynamicTariffTimerPicker'
import { useFormContext } from 'react-hook-form'
import { J2Multiply } from '@e3dc-react/icons'
import { UnitTypes } from 'src/Libs/types'
import { VariableGridFeesTranslationKey } from 'src/Libs/dynamic-tariff-settings/constants'

const useStyles = makeStyles()(theme => ({
  mobile: {
    background: theme.palette.grey.shadowLight,
    borderColor: theme.palette.grey.grey06,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2, 4),
  },
}))

interface Props {
  dateFrameName: string
  timeFrameIndex: number
  handleOpenConfirmationDialog: (index: number) => void
  hasWritingRights: boolean
}

/**
 * Component to set and edit time slots
 */
const TimeFrame: React.FC<Props> = ({ dateFrameName, timeFrameIndex, handleOpenConfirmationDialog, hasWritingRights }) => {
  const { t } = useCustomTranslation()
  const { isMobile: isDownSm } = useApp()
  const { classes } = useStyles()
  const { getValues } = useFormContext()

  const timeFrameName = `${dateFrameName}.timeFrames.${timeFrameIndex}`
  const timeFrames = getValues(`${dateFrameName}.timeFrames`)

  const isFirstTimeFrame = timeFrameIndex === 0 || !hasWritingRights
  const isLastTimeFrame = timeFrameIndex === timeFrames.length - 1 || !hasWritingRights

  const fromField = <DynamicTariffTimerPicker name={`${timeFrameName}.from`} disabled={isFirstTimeFrame} />
  const toField = <DynamicTariffTimerPicker name={`${timeFrameName}.to`} disabled={isLastTimeFrame} />
  const descriptionField = <TextField disabled={!hasWritingRights} fullWidth name={`${timeFrameName}.description`} />
  const priceField = (
    <NumberField
      disabled={!hasWritingRights}
      name={`${timeFrameName}.price`}
      suffix={UnitTypes.centsPerKiloWattHour}
      inputProps={{ style: { textAlign: 'right' } }}
    />
  )

  if (isDownSm) {
    return (
      <Grid key={0} container spacing={0} rowGap={2} className={classes.mobile}>
        <Grid item xs={6} display="flex" alignItems="center">
          <Typography variant="body1">{t(VariableGridFeesTranslationKey.From)}</Typography>
        </Grid>
        <Grid item xs={6}>
          {fromField}
        </Grid>

        <Grid item xs={6} display="flex" alignItems="center">
          <Typography variant="body1">{t(VariableGridFeesTranslationKey.Until)}</Typography>
        </Grid>
        <Grid item xs={6}>
          {toField}
        </Grid>

        <Grid item xs={12} display="flex" alignItems="center">
          <Typography variant="body1">{t(VariableGridFeesTranslationKey.Fees)}</Typography>
        </Grid>
        <Grid item xs={12}>
          {priceField}
        </Grid>

        <Grid item xs={12} display="flex" alignItems="center">
          <Typography variant="body1">{t(VariableGridFeesTranslationKey.Description)}</Typography>
        </Grid>
        <Grid item xs={12}>
          {descriptionField}
        </Grid>

        <Grid item xs={12} display="flex" justifyContent="end">
          <Button
            disabled={!hasWritingRights}
            sx={{ textTransform: 'uppercase', color: 'grey.grey00' }}
            onClick={() => handleOpenConfirmationDialog(timeFrameIndex)}
            variant="text"
          >
            {t('common.remove')}
          </Button>
        </Grid>
      </Grid>
    )
  }
  return (
    <Grid key={1} container spacing={2}>
      <Grid item xs={2} md={2}>
        {fromField}
      </Grid>
      <Grid item xs={2} md={2}>
        {toField}
      </Grid>
      <Grid item xs={2} md={2}>
        {priceField}
      </Grid>
      <Grid item xs={6} md={6}>
        <Stack direction="row" gap={{ sm: 2 }}>
          {descriptionField}
          <IconButton disabled={!hasWritingRights} sx={{ color: 'grey.grey00' }} onClick={() => handleOpenConfirmationDialog(timeFrameIndex)}>
            <J2Multiply />
          </IconButton>
        </Stack>
      </Grid>
    </Grid>
  )
}

export default TimeFrame
